import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Urls } from 'data';
import { getOrganizationDisplayLabel } from 'helpers';
import { useAPI, useGetUser } from 'hooks';
import { PermissionsContext, PreferencesContext } from 'context';
import Locales from 'locales';
import {
  ChartColumnIncreasing,
  Users,
  Building2,
  ShieldUser,
  LogOut,
  Settings,
  Menu,
  X,
  FileText,
  FileCheck,
  UserCheck,
  User,
  Check,
  ChevronUp,
  CircleUserRound,
  LockKeyhole,
} from 'lucide-react';
import { cn } from 'lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import logo from 'assets/img/logo/icon_ehadj_green.svg';
import AGLOCampaignSelector from 'components/shared/AGLOCampaignSelector';

const SIDEBAR_QUERY_KEYS_TO_INVALIDATE = [
  ['trip/'],
  ['trip/stats/default/'],
  ['trip/stats/trip_category_paid/'],
  ['invoice/'],
  ['agency/'],
  ['pilgrim/group/agency/'],
  ['user/'],
  ['permission/current/'],
  ['preference/current/'],
];

const getInitials = (name) => {
  const nameParts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (nameParts.length === 0) {
    return '?';
  }

  if (nameParts.length === 1) {
    return nameParts[0].slice(0, 2).toUpperCase();
  }

  return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
};

const getMembershipLabels = (membership) => {
  const organizationName = typeof membership?.organizationName === 'string' ? membership.organizationName.trim() : '';
  const displayName = getOrganizationDisplayLabel(organizationName) || organizationName || '-';

  return {
    displayName,
    fullName: organizationName && organizationName !== displayName ? organizationName : null,
  };
};

const SidebarComponent = ({ onClose = null }) => {
  const collapsed = false;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    user,
    isAgency,
    isAgencyPrimary,
    isBank,
    isAGLO,
    isSuperAdmin,
    isPrimaryUser,
    isMS,
    isDEI,
    isMAEC,
    canAccessDashboard,
    memberships,
    activeOrganizationId,
    activeOrganizationName,
    refreshCurrentUser,
  } = useGetUser();
  const { isPlatformLocked } = useContext(PermissionsContext);
  const { campaignYear } = useContext(PreferencesContext);
  const { first_name: firstname, last_name: lastname } = user || {};
  const { api } = useAPI();
  const userName = [firstname, lastname]
    .filter((namePart) => typeof namePart === 'string' && namePart.trim())
    .map((namePart) => namePart.trim())
    .join(' ');
  const safeUserName = userName || 'Utilisateur';
  const activeOrganizationLabel = getOrganizationDisplayLabel(activeOrganizationName) || '-';

  const organizationOptions = useMemo(
    () => memberships.filter((membership) => membership.organizationId),
    [memberships]
  );

  const { mutate: switchOrganization, isLoading: isSwitchingOrganization } = useMutation({
    mutationFn: (organizationId) =>
      api.post('user/' + user.id + '/switch_organization/?organization_id=' + organizationId),
    onSuccess: async () => {
      // Refresh the current user first so route/menu booleans recompute from the
      // backend-confirmed active organization before we invalidate page data.
      await refreshCurrentUser();
      // Trips, invoices, and related views are organization-scoped,
      // so switching org must invalidate their cached results.
      await Promise.all(
        SIDEBAR_QUERY_KEYS_TO_INVALIDATE.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
      );
      toast.success('Organisation active mise a jour.');
    },
    onError: () => {
      toast.error("Impossible de changer l'organisation active.");
    },
  });

  const isActive = (itemPath) => location.pathname === itemPath;

  const menuGroups = [
    {
      label: Locales.base.sidebar.pilgrim_management,
      items: [
        (isAgency || isAGLO || isMS || isDEI || isMAEC) && {
          icon: User,
          label: Locales.base.sidebar.trips,
          link: Urls.trip,
          active: isActive(Urls.trip),
        },
        (isAgency || isAGLO) && {
          icon: Users,
          label: Locales.base.sidebar.groups,
          link: Urls.group,
          active: isActive(Urls.group),
        },
        (isAgency || isAGLO || isBank) && {
          icon: FileText,
          label: Locales.base.sidebar.invoices,
          link: Urls.invoice,
          active: isActive(Urls.invoice),
        },
      ].filter(Boolean),
    },
    {
      label: Locales.base.sidebar.agency_management,
      items: [
        isAGLO && {
          icon: Building2,
          label: Locales.base.sidebar.agencies,
          link: Urls.agency,
          active: isActive(Urls.agency),
        },
        isAGLO && {
          icon: UserCheck,
          label: Locales.base.sidebar.agency_assignation,
          link: Urls.assignation,
          active: isActive(Urls.assignation),
        },
        isAgencyPrimary && {
          icon: FileCheck,
          label: Locales.base.sidebar.authorization,
          link: Urls.authorization,
          active: isActive(Urls.authorization),
        },
      ].filter(Boolean),
    },
    {
      label: Locales.base.sidebar.analytics,
      items: [
        canAccessDashboard && {
          icon: ChartColumnIncreasing,
          label: Locales.base.sidebar.dashboard,
          link: Urls.dashboard,
          active: isActive(Urls.dashboard),
        },
      ].filter(Boolean),
    },
    {
      label: Locales.base.sidebar.configuration,
      items: [
        isPrimaryUser && {
          icon: ShieldUser,
          label: Locales.base.sidebar.users,
          link: Urls.user,
          active: isActive(Urls.user),
        },
        (isSuperAdmin || isAGLO) && {
          icon: Settings,
          label: Locales.base.sidebar.preferences,
          link: Urls.preferences,
          active: isActive(Urls.preferences),
        },
      ].filter(Boolean),
    },
  ].filter((group) => group.items.length > 0);

  const handleClose = () => {
    setIsAccountMenuOpen(false);
    setIsMobileOpen(false);
    if (onClose) onClose();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  };

  const handleLogout = () => {
    setIsAccountMenuOpen(false);
    navigate(Urls.front.logout);
  };

  const handleProfile = () => {
    setIsAccountMenuOpen(false);
    navigate(Urls.userProfile);

    if (isMobileOpen) {
      handleClose();
    }
  };

  const handleOrganizationSwitch = (organizationId) => {
    if (!organizationId || organizationId === activeOrganizationId || isSwitchingOrganization) {
      return;
    }

    setIsAccountMenuOpen(false);
    switchOrganization(organizationId);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 flex h-16 items-center gap-3 border-b border-gray-200 bg-white px-4 lg:hidden">
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setIsMobileOpen(true)}
          className="rounded-md p-2 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary"
        >
          <Menu className="w-6 h-6 text-gray-700" />
        </button>
        <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src={logo} alt="eHadj" className="h-8 w-8 object-contain" />
            <span className="-mb-1 text-2xl font-bold text-green-800">eHadj</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
            {campaignYear && !isAGLO && <span>{campaignYear}</span>}
            {isPlatformLocked && (
              <span title="Plateforme verrouillée">
                <LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">Plateforme verrouillée</span>
              </span>
            )}
          </div>
        </div>
      </header>

      {isMobileOpen && (
        <div
          className={cn('fixed inset-0 z-40 bg-black/50 lg:hidden', 'animate-in fade-in duration-300')}
          onClick={handleClose}
          role="button"
          tabIndex={0}
          aria-label="Fermer le menu"
          onKeyDown={handleKeyDown}
        />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 flex h-screen flex-col border-r border-[#e2e7e5] bg-[#f9fafb]',
          'transition-all duration-500 ease-in-out',
          collapsed ? 'lg:w-24' : 'lg:w-[224px]',
          isMobileOpen ? 'w-full translate-x-0 lg:w-[224px]' : 'translate-x-[-100%] lg:translate-x-0'
        )}
      >
        <div
          className={cn(
            'relative flex h-[68px] shrink-0 items-center px-[18px]',
            collapsed ? 'flex flex-col items-center' : 'flex items-center justify-between'
          )}
        >
          <div className="flex w-full items-center gap-2">
            {!collapsed && (
              <div className="flex w-full items-center justify-between gap-3 pr-10 lg:pr-0">
                <div className="flex items-center gap-1">
                  <img src={logo} alt="eHadj" className="h-10 w-10" />
                  <span className="-mb-1 text-2xl font-bold text-green-800">eHadj</span>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] font-medium tracking-[0.02em] text-[#67726b]">
                  {campaignYear && !isAGLO && <span>{campaignYear}</span>}
                  {isPlatformLocked && (
                    <span title="Plateforme verrouillée">
                      <LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
                      <span className="sr-only">Plateforme verrouillée</span>
                    </span>
                  )}
                </div>
              </div>
            )}
            {collapsed && <img src={logo} alt="eHadj" className="h-8 w-8" />}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Fermer le menu"
              onClick={handleClose}
              className="absolute right-4 top-[18px] rounded-md p-2 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary lg:hidden"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-[10px] pb-5 pt-[10px]" aria-label="Navigation principale">
          {isAGLO && <AGLOCampaignSelector />}
          {menuGroups.map((group, groupIndex) => (
            <section key={group.label} className={cn(groupIndex > 0 && 'mt-5')}>
              {!collapsed && (
                <h2 className="mb-[7px] px-[11px] text-[10px] font-medium leading-[14px] tracking-[0.065em] text-[#747d78]">
                  {group.label}
                </h2>
              )}
              {group.items.map((item) => {
                const Icon = item.icon;

                return (
                  <NavLink
                    key={item.label}
                    to={item.link}
                    onClick={() => isMobileOpen && handleClose()}
                    className={cn(
                      'my-0.5 flex h-9 w-full items-center gap-2.5 rounded-md px-[11px] text-[13px] font-medium leading-[18px] text-[#3e4742] transition-colors hover:bg-[#edf0ee] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary focus-visible:ring-offset-1',
                      collapsed && 'justify-center px-0',
                      item.active && 'bg-[#e7efea] font-semibold text-[#18543b] hover:bg-[#e7efea]'
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-4 w-4 shrink-0 text-[#717b74]',
                        item.active && 'text-[#256547] [stroke-width:1.85]'
                      )}
                      aria-hidden="true"
                    />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </section>
          ))}
        </nav>

        <div className="shrink-0 px-[10px] pb-3 pt-[10px] before:mb-[10px] before:ml-[3px] before:block before:h-px before:w-[calc(100%-6px)] before:bg-[#e1e7e3]">
          <Popover open={isAccountMenuOpen} onOpenChange={setIsAccountMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-expanded={isAccountMenuOpen}
                aria-label={`Compte de ${safeUserName}, organisation active : ${activeOrganizationLabel}`}
                className={cn(
                  'grid w-full grid-cols-[30px_minmax(0,1fr)_13px] items-center gap-[9px] rounded-[7px] bg-[#eff2f0] px-[7px] py-[9px] text-left text-[#354139] transition-colors hover:bg-[#e6ece7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary focus-visible:ring-offset-1',
                  isAccountMenuOpen && 'bg-[#e6ece7]',
                  collapsed && 'flex justify-center'
                )}
              >
                <span
                  className="mt-px flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-[#e5ede7] text-[11px] font-semibold tracking-[0.01em] text-[#3e624d]"
                  aria-hidden="true"
                >
                  {getInitials(userName)}
                </span>
                {!collapsed && (
                  <>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-xs font-semibold leading-[18px]">{safeUserName}</span>
                      <span className="mt-px truncate text-[11px] font-medium leading-[17px] text-[#56675b]">
                        {activeOrganizationLabel}
                      </span>
                    </span>
                    <ChevronUp
                      className={cn(
                        'h-[13px] w-[13px] shrink-0 text-[#617365] transition-transform',
                        isAccountMenuOpen && 'rotate-180'
                      )}
                      aria-hidden="true"
                    />
                  </>
                )}
              </button>
            </PopoverTrigger>

            <PopoverContent
              side="top"
              align="start"
              sideOffset={2}
              collisionPadding={12}
              aria-label="Compte et organisations"
              className="flex max-h-[min(420px,calc(100dvh-110px))] w-[244px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-[9px] border-[#dce3de] p-0 shadow-[0_6px_20px_rgba(32,56,43,0.07),0_2px_4px_rgba(32,56,43,0.03)]"
            >
              <div className="shrink-0 border-b border-[#edf0ee] px-3 pb-2.5 pt-[11px]">
                <p className="break-words text-[13px] font-semibold leading-5 text-[#354139]">{safeUserName}</p>
                <p className="mt-px break-all text-[11px] leading-[17px] text-[#727e74]">{user?.email || '-'}</p>
              </div>

              {organizationOptions.length > 1 && (
                <>
                  <p className="shrink-0 px-3 pb-1.5 pt-2.5 text-[10px] font-medium leading-[15px] tracking-[0.045em] text-[#737d75]">
                    {Locales.base.sidebar.change_organization}
                  </p>

                  <div
                    className="min-h-0 max-h-[246px] shrink overflow-y-scroll overscroll-contain pb-1 pl-1.5 pr-1 [scrollbar-color:#e6ebe7_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin] hover:[scrollbar-color:#c9d3cc_transparent] focus-within:[scrollbar-color:#c9d3cc_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#e6ebe7] [&:hover::-webkit-scrollbar-thumb]:bg-[#c9d3cc] [&:focus-within::-webkit-scrollbar-thumb]:bg-[#c9d3cc] [&::-webkit-scrollbar-track]:bg-transparent"
                    role="group"
                    aria-label={`Organisations disponibles, ${organizationOptions.length} appartenances`}
                  >
                    {organizationOptions.map((organization) => {
                      const isActiveOrganization = organization.organizationId === activeOrganizationId;
                      const { displayName, fullName } = getMembershipLabels(organization);

                      return (
                        <button
                          key={organization.organizationId}
                          type="button"
                          aria-pressed={isActiveOrganization}
                          disabled={isSwitchingOrganization}
                          onClick={() => handleOrganizationSwitch(organization.organizationId)}
                          className={cn(
                            'my-0.5 grid min-h-10 w-full grid-cols-[minmax(0,1fr)_14px] items-center gap-[7px] rounded-md bg-transparent p-[7px] text-left text-[#344239] transition-colors hover:bg-[#f0f3f0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary disabled:cursor-wait',
                            isActiveOrganization && 'bg-[#eaf2ec] text-[#225b3c] hover:bg-[#eaf2ec]'
                          )}
                        >
                          <span className="flex min-w-0 flex-col gap-0.5">
                            <span
                              className={cn(
                                'text-xs font-medium leading-[17px]',
                                isActiveOrganization && 'font-semibold'
                              )}
                            >
                              {displayName}
                            </span>
                            {fullName && (
                              <span className="line-clamp-2 break-words text-[11px] leading-[15px] text-[#69776e]">
                                {fullName}
                              </span>
                            )}
                          </span>
                          {isActiveOrganization && (
                            <Check className="h-3.5 w-3.5 text-[#2a6948] [stroke-width:2]" aria-hidden="true" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <div className="shrink-0 border-t border-[#edf0ee] px-1.5 py-1">
                <button
                  type="button"
                  onClick={handleProfile}
                  className="flex h-8 w-full items-center gap-[9px] rounded-md px-[7px] text-left text-xs text-[#455349] hover:bg-[#f4f6f4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary"
                >
                  <CircleUserRound className="h-[15px] w-[15px] text-[#6e7b72]" aria-hidden="true" />
                  <span>{Locales.base.sidebar.profile}</span>
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex h-8 w-full items-center gap-[9px] rounded-md px-[7px] text-left text-xs text-[#aa4545] hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  <LogOut className="h-[15px] w-[15px] text-[#b96060]" aria-hidden="true" />
                  <span>{Locales.base.sidebar.logout}</span>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </aside>
    </>
  );
};

SidebarComponent.propTypes = {
  onClose: PropTypes.func,
};

export default SidebarComponent;
